import { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { Controller, FieldValues, Path, UseFormReturn, FieldName, FieldErrors } from "react-hook-form";
import { DocumentStructure } from "../../../../../models/document-structure";
import { ErrorMessage, FieldValuesFromFieldErrors } from "@hookform/error-message"
import { CssField } from "../../MuiInput.styled";
import { getValueByPath } from "@shared/lib/utils";
import { AccountStructure } from "../../../../../models/accounts-structure";
import { Item } from "../../../../../models/item";
import { searchItems } from "../../../../../features/tickets/api/items.service";
import { buildAutocompleteFilter } from "../buildAutocompleteFilter";
import { InputLabel } from "@mui/material";

interface ItemSearchProps<T extends FieldValues> {
    fieldStructure: AccountStructure | DocumentStructure, // field data
    useForm: UseFormReturn<T>; // form
    onSelect: (object: Item) => void, // update the form after object selected
    prefixPath?: string, // prefix path for the field in the form
    disabled?: boolean, // disable the field
    minWidth?: number,
}

function ItemSearch<T extends FieldValues>({ fieldStructure, useForm, onSelect, prefixPath, disabled, minWidth=150}: ItemSearchProps<T>) {

    
    const formPath = (prefixPath? prefixPath + "." : "" ) + fieldStructure.field_name;

    const [options, setOptions] = useState<Item[]>([]);
    const [inputValue, setInputValue] = useState("");
    

    useEffect(() => {

        if (inputValue.length < 1) {
            setOptions([]);
            return;
        }

        const controller = new AbortController();

        (async () => {
            try {

                const path = fieldStructure.field_name;
                const items = await fetchItems(path as Path<T>, inputValue, controller.signal);

                if (!controller.signal.aborted) {
                    setOptions(items);
                }
            } catch (e) {
                if (!controller.signal.aborted) {
                    console.error(e);
                }
            }
        })();

        return () => {
            controller.abort();
        };

    }, [inputValue, fieldStructure.field_name]);


    /**
     * search items by key and value
     * @param key - account key name 
     * @param value - value for search
     * @returns items
     */
    async function fetchItems(key: Path<T>, value: string, signal?: AbortSignal): Promise<Item[]> {
        const response = await searchItems(
            {
                filter: buildAutocompleteFilter(String(key), value),
                per_page: 5,
                sort: ["id"],
            },
            signal,
        );

        return response.data;
    }


    /**
     * update the form with a new value
     * @param val - the new value, can be a specific field value or the whole object itself
     */
    function onChange(val: string | Item | null): void {

        if (typeof val === "string") {
            useForm.setValue(formPath as  Path<T>, val as any);
            return;
        }

        if (val === null) {
            useForm.setValue(formPath as  Path<T>, null as any); // or reset relevant fields
            return;
        }

        onSelect(val);
    }


    function renderOption(option: Item): string {

        if(fieldStructure.field_name === "sku"){
            return String(getValueByPath(option, fieldStructure.field_name as keyof Item) + ": " + option["name" as keyof Item]);
        }

        return String(option["sku" as keyof Item]+ ": " + String(getValueByPath(option, fieldStructure.field_name as keyof Item)));

    }


    function getValueByFieldName(item: Item): string {
        const path = fieldStructure.field_name;
        const value = getValueByPath(item, path)
        return String(value);
    }

    return (
        <CssField sx={{ minWidth }} error={useForm?.formState?.errors?.[fieldStructure.field_name] ? true : false}>
            <Controller
                name={formPath as Path<T>}
                control={useForm.control}
                render={({ field }) => (
                    <Autocomplete<Item, false, false, true>
                    {...field}
                    freeSolo
                    clearOnBlur={false}
                    options={options}
                    value={null}
                    getOptionLabel={(option) =>typeof option === "string" ? option : getValueByFieldName(option)}
                    inputValue={String(field.value ?? "")}
                    onChange={(_, newValue)=> onChange(newValue)}
                    disabled={disabled}

                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {renderOption(option)}
                        </li>
                    )}

                    onInputChange={(_, newInput) => {
                        field.onChange(newInput);
                        setInputValue(newInput);
                    }}

                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={fieldStructure.field_display_name}
                            error={useForm?.formState?.errors?.[fieldStructure.field_name] ? true : false}
                            helperText={<ErrorMessage errors={useForm.formState.errors} name={fieldStructure.field_name as FieldName<FieldValuesFromFieldErrors<FieldErrors<T>>>} />}
                        />
                    )}
                    />
                )}
            />
        </CssField>
        
    );
}

export default ItemSearch;